import type { WorkspaceKey } from '@festina-life/api'
import {
  assertNotEmpty,
  type ConfigurableServer,
  type Optional,
} from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import { useQuery } from '@tanstack/react-query'

import { useMetaModelContext } from '../../../context/metaModelContext'
import { useService } from '../../../hooks/useService'
import { fetchAllWorkspaceBranchChangesetsKey } from '../keys'

export const useFetchWorkspaceBaseBranchChangesets = (
  workspaceKey: Optional<WorkspaceKey>,
) => {
  const { blockTopology } = useMetaModelContext()
  const translate = useTranslate()
  const workspaceName = workspaceKey?.name
  const server = workspaceKey?.branchKey.server
  const service = useService(server)
  const rootBlock = blockTopology?.name
  assertNotEmpty(rootBlock, translate('error.noBlocks'))

  // We intentionally use useQuery in order to avoid suspense from kicking in inside the branches panel,
  // making it snappier for users to click through branch in the branches tree.
  return useQuery({
    enabled: !!workspaceName && !!server && !!service,
    queryKey: fetchAllWorkspaceBranchChangesetsKey({
      workspaceName: workspaceName ?? '',
      componentName: server ?? ('' as ConfigurableServer),
      baseBranchName: workspaceKey?.branchKey.name ?? '',
    }),
    queryFn: async () => {
      assertNotEmpty(workspaceName)
      assertNotEmpty(server)
      assertNotEmpty(service)

      return await service.api.ConfigurationRepositoryWorkspacesService.workspaceServiceGetGetWorkspaceBaseBranchChangesets(
        {
          componentName: server.toLocaleLowerCase(),
          workspaceName,
          skipLargeOperationTranslation: true,
        },
      )
    },
  })
}
